"use client";
import React from 'react';
import { motion } from 'framer-motion';

const industries = [
    { label: 'SaaS & Technology Companies', icon: '⬡' },
    { label: 'Ecommerce & D2C Brands', icon: '◇' },
    { label: 'Healthcare & Wellness Clinics', icon: '◎' },
    { label: 'Real Estate Developers', icon: '◈' },
    { label: 'Professional Services', icon: '◆' },
    { label: 'B2B Service Businesses', icon: '▣' },
];

const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 32, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
};

export default function SEOAudience() {
    return (
        <section className="relative overflow-hidden py-10 md:py-28 px-4 md:px-16 lg:px-24"
            style={{ background: '#080808' }}>

            {/* Dot grid */}
            <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
            }} />

            {/* Top lime glow */}
            <div className="absolute pointer-events-none" style={{
                top: '-80px', left: '50%', transform: 'translateX(-50%)',
                width: 600, height: 300,
                background: 'radial-gradient(ellipse, rgba(206,255,26,0.07) 0%, transparent 70%)',
            }} />

            <div className="max-w-[1360px] mx-auto relative z-10 flex flex-col gap-10 md:gap-20">

                {/* ── Header ── */}
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-24 items-start">

                    {/* Left: label + heading */}
                    <div className="flex-1 flex flex-col gap-5">
                        <motion.div
                            initial={{ opacity: 0, x: -16 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-3">
                            <div style={{ width: 28, height: 2, background: '#ceff1a', borderRadius: 2 }} />
                            <span style={{ color: '#ceff1a', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                                Who We Work With
                            </span>
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: 0.08 }}
                            className="font-black leading-[1.05] tracking-tight"
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff', fontSize: 'clamp(24px, 2.6vw, 40px)' }}>
                            SEO Built For<br /><span style={{ color: '#ceff1a' }}>Sustainable Growth.</span>
                        </motion.h2>
                    </div>

                    {/* Right: body copy */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex-1 flex flex-col gap-6 justify-center pt-2">
                        <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.5)', fontSize: 17, lineHeight: 1.82 }}>
                            We provide{' '}
                            <span style={{ color: '#ffffff', fontWeight: 600 }}>SEO services in Bangalore</span>
                            {' '}for businesses focused on sustainable growth.
                        </p>
                        <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.5)', fontSize: 17, lineHeight: 1.82 }}>
                            Every industry searches differently. Every customer journey is unique.{' '}
                            <span style={{ color: '#ceff1a', fontWeight: 600 }}>Our strategies are built around how your customers discover, evaluate, and choose solutions.</span>
                        </p>
                    </motion.div>
                </div>

                {/* ── Industry cards grid ── */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-60px' }}
                    className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {industries.map((ind, i) => (
                        <motion.div
                            key={ind.label}
                            variants={cardVariants}
                            whileHover={{ y: -6, borderColor: 'rgba(206,255,26,0.45)', transition: { duration: 0.22 } }}
                            style={{
                                background: i % 3 === 0
                                    ? 'rgba(206,255,26,0.04)'
                                    : i % 3 === 1
                                        ? 'rgba(55,48,163,0.08)'
                                        : 'rgba(255,255,255,0.02)',
                                border: '1px solid rgba(255,255,255,0.07)',
                                borderRadius: 16,
                                padding: '22px 20px',
                                cursor: 'default',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                            }}>
                            {/* Icon bubble */}
                            <div style={{
                                width: 40, height: 40, borderRadius: 10,
                                background: i % 3 === 0 ? 'rgba(206,255,26,0.12)' : i % 3 === 1 ? 'rgba(55,48,163,0.2)' : 'rgba(255,255,255,0.05)',
                                border: `1px solid ${i % 3 === 0 ? 'rgba(206,255,26,0.25)' : i % 3 === 1 ? 'rgba(55,48,163,0.4)' : 'rgba(255,255,255,0.08)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 16,
                                color: i % 3 === 0 ? '#ceff1a' : i % 3 === 1 ? '#9b59f5' : 'rgba(255,255,255,0.4)',
                            }}>
                                {ind.icon}
                            </div>
                            <span style={{
                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                color: 'rgba(255,255,255,0.75)',
                                fontSize: 14,
                                fontWeight: 600,
                                lineHeight: 1.4,
                                letterSpacing: '0.01em',
                            }}>
                                {ind.label}
                            </span>
                            {/* Bottom accent line */}
                            <div style={{
                                height: 2, borderRadius: 2, marginTop: 'auto',
                                background: i % 3 === 0
                                    ? 'linear-gradient(90deg, #ceff1a, transparent)'
                                    : i % 3 === 1
                                        ? 'linear-gradient(90deg, #3730a3, transparent)'
                                        : 'linear-gradient(90deg, rgba(255,255,255,0.12), transparent)',
                                width: '60%',
                            }} />
                        </motion.div>
                    ))}
                </motion.div>

            </div>
        </section>
    );
}
